import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  TrendingUp,
  Heart,
  Zap,
  Shield,
} from "lucide-react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import AnimatedText from "../components/AnimatedText";
import GradientText from "../components/GradientText";
import VideoModal from "../components/VideoModal";
import CalendlyModal from "../components/CalendlyModal";

// Declare Calendly type for TypeScript
declare global {
  interface Window {
    Calendly: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

const HomePage: React.FC = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);

  // Function to open Calendly calendar
  // const openCalendly = () => {
  //   if (window.Calendly) {
  //     window.Calendly.initPopupWidget({
  //       url: 'https://calendly.com/zylusales/group-growth-consultation'
  //     });
  //   }
  // };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-white text-black py-20 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-100 to-red-200 rounded-full opacity-20 animate-float"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-gray-100 to-gray-200 rounded-full opacity-30 animate-bounce-subtle"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-red-50 to-gray-50 rounded-full opacity-40 animate-pulse"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-1"
            >
              <AnimatedText
                text="Salon Success"
                className="text-6xl md:text-8xl font-black leading-tight text-black"
                type="fade"
              />
              <AnimatedText
                text="On Autopilot"
                className="text-6xl md:text-8xl font-black leading-tight text-red-600 mb-6"
                type="slide"
                delay={0.3}
              />
              <AnimatedText
                text="Boost revenue 28%+ with complete automation"
                className="text-xl md:text-2xl text-gray-600 mb-8 font-medium"
                type="fade"
                delay={0.6}
              />

              <div className="flex flex-col md:flex-row gap-6 mb-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 }}
                >
                  <button
                    onClick={() => setIsCalendlyModalOpen(true)}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl text-center inline-block"
                  >
                    Book Now for Free - $99/- Consultation
                  </button>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative order-2 lg:order-2"
            >
              {/* Background Animation - Removed for pure white cards */}

              <div className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-200">
                <div className="grid grid-cols-2 gap-6">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-white p-6 rounded-xl text-center shadow-md border border-gray-100"
                  >
                    <TrendingUp className="h-12 w-12 mx-auto mb-4 text-red-600" />
                    <div className="text-3xl font-bold text-red-600 mb-2">
                      28%+
                    </div>
                    <p className="text-black font-semibold">Revenue Increase</p>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-white p-6 rounded-xl text-center shadow-md border border-gray-100"
                  >
                    <Users className="h-12 w-12 mx-auto mb-4 text-black" />
                    <div className="text-3xl font-bold text-black mb-2">
                      4-5x
                    </div>
                    <p className="text-black font-semibold">Client Retention</p>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-white p-6 rounded-xl text-center shadow-md border border-gray-100"
                  >
                    <Zap className="h-12 w-12 mx-auto mb-4 text-red-600" />
                    <div className="text-3xl font-bold text-red-600 mb-2">
                      15hrs
                    </div>
                    <p className="text-black font-semibold">Saved Weekly</p>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-white p-6 rounded-xl text-center shadow-md border border-gray-100"
                  >
                    <Heart className="h-12 w-12 mx-auto mb-4 text-black" />
                    <div className="text-3xl font-bold text-black mb-2">
                      98%
                    </div>
                    <p className="text-black font-semibold">Satisfaction</p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-red-600">
              Choose Your Path to Success
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Discover how Zylu can transform your specific business challenges
              into competitive advantages
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="h-full"
            >
              {/* <Link to="/revenue" className="block group"> */}
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform group-hover:scale-105 border border-gray-200 hover:border-red-600 h-full flex flex-col">
                <div className="bg-gray-100 p-4 rounded-lg w-fit mb-6 group-hover:bg-red-50 transition-colors">
                  <TrendingUp className="h-12 w-12 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  Boost Revenue by 28%
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed flex-grow">
                  Discover how automated bookings, smart pricing, and targeted
                  marketing can dramatically increase your salon's
                  profitability.
                </p>
                <div className="flex items-center text-red-600 font-semibold group-hover:text-red-700 mt-auto">
                  {/* Learn More <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" /> */}
                </div>
              </div>
              {/* </Link> */}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="h-full"
            >
              {/* <Link to="/productivity" className="block group"> */}
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform group-hover:scale-105 border border-gray-200 hover:border-red-600 h-full flex flex-col">
                <div className="bg-gray-100 p-4 rounded-lg w-fit mb-6 group-hover:bg-red-50 transition-colors">
                  <Zap className="h-12 w-12 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  3X Team Productivity
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed flex-grow">
                  Eliminate manual tasks and streamline operations so your team
                  can focus on delivering exceptional customer experiences.
                </p>
                <div className="flex items-center text-red-600 font-semibold group-hover:text-red-700 mt-auto">
                  {/* Learn More <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" /> */}
                </div>
              </div>
              {/* </Link> */}
            </motion.div>

            {/* <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Link to="/retention" className="block group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform group-hover:scale-105 border border-gray-200 hover:border-red-600">
                  <div className="bg-white p-8 rounded-xl shadow-lg inline-block">
                    <div className="flex items-center justify-center mb-4">
                      <div className="flex mr-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-6 w-6 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <span className="text-2xl font-bold text-gray-900">4.9/5</span>
                    </div>
                    <p className="text-gray-600 text-lg">
                      Average rating from <strong>2,000+ salon owners</strong> worldwide
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div> */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="h-full"
            >
              {/* <Link to="/retention" className="block group"> */}
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform group-hover:scale-105 border border-gray-200 hover:border-red-600 h-full flex flex-col">
                <div className="bg-gray-100 p-4 rounded-lg w-fit mb-6 group-hover:bg-red-50 transition-colors">
                  <Heart className="h-12 w-12 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">
                  4-5X Client Retention
                </h3>
                <p className="text-gray-700 mb-6 leading-relaxed flex-grow">
                  Turn one-time visitors into loyal customers with automated
                  follow-ups, personalized experiences, and smart loyalty
                  programs.
                </p>
                <div className="flex items-center text-red-600 font-semibold group-hover:text-red-700 mt-auto">
                  {/* Learn More <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" /> */}
                </div>
              </div>
              {/* </Link> */}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Information Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="bg-gray-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8">
              <Shield className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-4xl font-bold mb-6 text-red-600">
              Transparent Pricing & Plans
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Explore our flexible pricing options designed to fit your salon's
              needs. Visit our pricing page for detailed information about plans
              and features.
            </p>
            <motion.div whileHover={{ scale: 1.05 }}>
              <a
                href="https://zylu.co/pricing-salon-growth-management-software/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all duration-300 transform hover:shadow-xl inline-block"
              >
                View Pricing & Plans
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 text-black py-12 mb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img
                src={`${window.location.origin}/logo-zylu.webp`}
                alt="Zylu Logo"
                className="h-10 bg-black"
              />
            </div>
            <p className="text-gray-600 mb-6">
              Empowering salon owners to run their business on autopilot
            </p>
            <div className="flex justify-center space-x-8 text-sm text-gray-600">
              <a href="#" className="hover:text-red-600 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-red-600 transition-colors">
                Terms of Service
              </a>
              <a href="#" className="hover:text-red-600 transition-colors">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="dQw4w9WgXcQ"
        videoType="youtube"
        title="2-Min Overview"
      />

      {/* Calendly Modal */}
      <CalendlyModal
        isOpen={isCalendlyModalOpen}
        onClose={() => setIsCalendlyModalOpen(false)}
        url="https://calendly.com/zylusales/zylu-growth-demo-free?hide_gdpr_banner=1"
        title="Schedule Your Free Consultation"
      />
    </div>
  );
};

export default HomePage;
